import * as React from 'react'
import styled, { DefaultTheme, ThemeProvider } from 'styled-components'
import BillboardChart from 'react-billboardjs'
import { ChartOptions, Data, DataItem, pie } from 'billboard.js'
import 'billboard.js/dist/billboard.css'
import { Box, muiBankTheme } from '@ally/metronome-ui'
import { SummarizedAccountGroup, GroupedAccounts } from '../../../types'
import BalanceChartTitle from './BalanceChartTitle'

interface BalanceChartProps {
  groupedAccounts: GroupedAccounts
  selected: string
  totalBalance: number
  onDataSelect: (accountGroup: string) => void
  resetSelected: (accountGroup: string) => void
  theme: DefaultTheme
  hasStaleData?: boolean
}

interface ColorLookupObj {
  [key: string]: string
}

interface BillboardChartConfig {
  data: Data
  options: ChartOptions
  domProps: Record<string, unknown>
}

const StyledChart = styled(BillboardChart)`
  .bb {
    &-chart {
      &-arc {
        cursor: pointer;
        path {
          stroke: none;
        }
      }
    }
  }
`

const displayBalance = (balance: number, totalBalance: number): number => {
  /* Make each data segment a min of 1% of the total balance */
  const minDisplayBalance = 0.01 * totalBalance
  return balance < minDisplayBalance ? minDisplayBalance : balance
}

const formatChartData = (
  groupedAccounts: SummarizedAccountGroup[],
  totalBalance: number,
): (string | number)[][] => {
  const data = groupedAccounts
    .filter(account => account.aggBalance > 0)
    .map(groupedAccount => {
      return [
        groupedAccount.accountGroup,
        displayBalance(groupedAccount.aggBalance, totalBalance),
      ]
    })
  return data
}

const dataColor = (color: string, d: DataItem, theme: DefaultTheme): string => {
  /**
   * Billboard.js is calling this function passing d
   * as an object of type DataItem AND as a string
   * with value of d.id. This behavior is not documented
   * and may be a bug. The const index is being used
   * to make sure we are only passing strings as the lookup index.
   *
   **/
  const index: string = typeof d === 'object' ? d.id : d
  const dataColors: ColorLookupObj = {
    INVW: theme.colors.invw,
    INVA: theme.colors.inva,
    INVS: theme.colors.inv,
    CHECKING: theme.colors.dda,
    SAVINGS: theme.colors.sda,
    CD: theme.colors.cda,
    IRA: theme.colors.ira,
  }
  return dataColors[index]
}

const getChartOptions = (props: BalanceChartProps): BillboardChartConfig => {
  const { groupedAccounts, totalBalance, onDataSelect, theme } = props
  return {
    data: {
      color: (color: string, d: DataItem): string => dataColor(color, d, theme),
      columns: formatChartData(Object.values(groupedAccounts), totalBalance),
      type: pie(),
      onclick: (d: DataItem): void => {
        if (onDataSelect) {
          onDataSelect(d.name || '')
        }
      },
    },
    options: {
      legend: {
        show: false,
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      pie: {
        label: {
          show: false,
        },
        innerRadius: 90,
      },
      size: {
        width: 260,
        height: 260,
      },
      tooltip: {
        show: false,
      },
    },
    domProps: {
      'data-type': 'chart',
      'aria-hidden': true,
    },
  }
}

const BalanceChart: React.FC<BalanceChartProps> = (
  props,
): React.ReactElement => {
  const { groupedAccounts, selected, resetSelected, hasStaleData } = props
  const { data, options, domProps } = getChartOptions(props)

  return (
    <ThemeProvider theme={muiBankTheme}>
      <Box
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <BalanceChartTitle
          accountGroup={selected}
          groupedAccounts={groupedAccounts}
          resetSelected={resetSelected}
          hasStaleData={hasStaleData}
        />
        <StyledChart
          data={data}
          legend={options.legend}
          padding={options.padding}
          pie={options.pie}
          size={options.size}
          tooltip={options.tooltip}
          domProps={domProps}
        />
      </Box>
    </ThemeProvider>
  )
}

export default BalanceChart
